import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";

const Shell = ({ title, subtitle, children, testId }) => (
  <div className="min-h-screen bg-background grid place-items-center px-4 py-10" data-testid={testId}>
    <div className="w-full max-w-md">
      <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="password-back-to-login">
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </Link>
      <img src="/logo-wordmark.png" alt="Basta10" className="h-24 w-auto -ml-1 mb-6" />
      <h1 className="font-heading text-2xl font-bold tracking-tight">{title}</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>
      {children}
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Shell testId="forgot-password-page" title="Forgot your password?" subtitle="Enter the email you signed up with and we'll send a reset link.">
      {sent ? (
        <div className="surface p-5 flex gap-3" data-testid="forgot-password-sent">
          <MailCheck className="h-6 w-6 text-emerald-600 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">Check your inbox</div>
            <p className="text-muted-foreground mt-1">If an account exists for <b>{email}</b>, a reset link is on its way from invoices@basta10.com. It expires in 1 hour — check spam if it doesn't show up in a minute.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input data-testid="forgot-password-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" />
          </div>
          <Button data-testid="forgot-password-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {busy ? "Sending..." : "Email me a reset link"}
          </Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords don't match");
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: f.password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      toast.success("Password updated — you're signed in");
      navigate("/clients");
    } catch (error) { setErr(errMsg(error)); } finally { setBusy(false); }
  };

  return (
    <Shell testId="reset-password-page" title="Choose a new password" subtitle="Use at least 8 characters. You'll be signed in right after.">
      {!token ? (
        <p className="text-sm text-red-600 font-medium" data-testid="reset-password-missing-token">This link is missing its token. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input data-testid="reset-password-input" type="password" required minLength={8} autoFocus value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <Input data-testid="reset-password-confirm-input" type="password" required minLength={8} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
          </div>
          {err && <p data-testid="reset-password-error" className="text-sm text-red-600 font-medium">{err} {err.includes("expired") && <Link to="/forgot-password" className="underline">Request a new link</Link>}</p>}
          <Button data-testid="reset-password-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Set new password"}
          </Button>
        </form>
      )}
    </Shell>
  );
}
